import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { chipsFromRepeat, toggleWeekday, WEEKDAY_CHIPS } from './cron-weekly.util';

let nextLabelId = 0;

/**
 * Weekday toggles (Ma-Zo) for a weekly cron. Presentational: the owner passes the ticked days in and
 * writes the reported selection back (as a user edit, so the form turns dirty).
 */
@Component({
  selector: 'dd-cron-weekday-chips',
  template: `
    <div class="dd-weekday-label" [id]="labelId" data-cy="cron-weekday-label">Draait op</div>
    <div
      class="dd-weekday-chips"
      role="group"
      [attr.aria-labelledby]="labelId"
      [attr.aria-describedby]="describedBy()"
      data-cy="cron-weekday-chips">
      @for (chip of chips(); track chip.value) {
        <button
          type="button"
          class="dd-weekday-chip"
          [attr.aria-pressed]="chip.pressed"
          [attr.aria-label]="chip.name"
          [attr.data-cy]="'cron-weekday-' + chip.value"
          [disabled]="disabled()"
          (click)="toggle(chip.value)">
          @if (chip.pressed) {
            <mat-icon class="dd-weekday-chip__check" svgIcon="heroicons_outline:check" aria-hidden="true"></mat-icon>
          }
          {{ chip.label }}
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
    .dd-weekday-label {
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
      color: var(--dd-field-label);
    }
    .dd-weekday-chips {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      /* The chips are 32px and the date field's input box 36px: centre them on that box. */
      min-height: 36px;
      padding-bottom: 16px;
    }
    /* Figma chip: h32, 1px outline, radius 8, 12px Medium. The field tokens keep the outline visible in dark. */
    .dd-weekday-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      /* min-width fits label plus the 14px check, so the row does not jump when a chip is pressed;
         seven of them still fit one row next to the 244px "Vanaf" column. */
      box-sizing: border-box;
      min-width: 58px;
      height: 32px;
      padding: 0 8px;
      /* Figma: unselected border = --fuse-border (#EEEFF1), text #545559. */
      border: 1px solid var(--fuse-border);
      border-radius: 8px;
      background: var(--fuse-bg-card);
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      color: var(--dd-accent);
    }
    .dd-weekday-chip:disabled {
      cursor: default;
      opacity: 0.6;
    }
    .dd-weekday-chip:hover:not(:disabled) {
      background: color-mix(in srgb, currentColor 6%, transparent);
    }
    /* Same accent tokens as the route marker (_status-badges.scss); each theme defines its own values.
       Figma: label and check both in the accent, on the accent fill. */
    .dd-weekday-chip[aria-pressed='true'] {
      border-color: var(--dd-badge-accent-border);
      background: var(--dd-badge-accent-bg);
      color: var(--dd-badge-accent-fg);
    }
    .dd-weekday-chip__check {
      width: 14px;
      height: 14px;
      min-width: 14px;
      font-size: 14px;
      color: var(--dd-badge-accent-fg);
    }
    /* Figma's check is heavier than the heroicons outline default (1.5). ::ng-deep: the svg is
       inserted by MatIconRegistry and carries no encapsulation attribute. */
    .dd-weekday-chip__check ::ng-deep path {
      stroke-width: 2;
    }
    .dd-weekday-chip:focus-visible {
      outline: 2px solid currentColor !important;
      outline-offset: 1px;
    }
  `,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CronWeekdayChipsComponent {
  protected readonly labelId = `dd-weekday-label-${nextLabelId++}`;

  /** The ticked days, as `days-of-week` values ('0' = Sunday). */
  readonly selected = input<readonly (string | number)[] | null>([]);
  /** The new selection after a toggle, in chip order. */
  readonly selectedChange = output<string[]>();
  /** Id of the message that explains why the selection is not valid; announced with the group (aria-invalid is not allowed on role=group). */
  readonly describedBy = input<string | null>(null);
  /** The repeat is read-only for this user: the days show, a click changes nothing. */
  readonly disabled = input(false);

  protected readonly chips = computed(() => {
    const selected = chipsFromRepeat(this.selected());
    return WEEKDAY_CHIPS.map(chip => ({ ...chip, pressed: selected.includes(chip.value) }));
  });

  protected toggle(value: string) {
    this.selectedChange.emit(toggleWeekday(this.selected(), value));
  }
}
